import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip, ChipBackgroundColor } from "@amzn/stencil-react-components/chip";
import {
  Expander,
  ExpanderChevronPosition,
  ExpanderTitleRendererParams,
} from "@amzn/stencil-react-components/expander";
import { Col, GridContainer, GridItem, Hr, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { ModalContent, RenderModalFunctionParams, WithModal } from "@amzn/stencil-react-components/modal";
import { Pagination } from "@amzn/stencil-react-components/pagination";
import { Text } from "@amzn/stencil-react-components/text";
import { IconGlobe, IconPadlock, IconPeople } from "@amzn/stencil-react-icons";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil";
import { resolveDisplayName } from "common/components/AIForMe/helpers";
import ListModalButton, { LIST_MODAL_TYPE } from "common/components/ListModal/ListModalButton";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getJobFamilyFromRule, getLinkToDetails } from "common/helpers/lists-helper";
import { getString } from "common/uistringlabels/uiStringUtils";
import { ListModel, Visibility } from "lib/models/list-models";
import { UserPermissions } from "lib/services";
import { ItemModel } from "lib/services/catalogical-models";
import React, { FC, useState } from "react";

import { ListItem, ListItemType } from "./ListItem";

export const normalizeListItem = (item: ItemModel, domain?: string): ListItemType => ({
  name:
    domain === "aiforme"
      ? resolveDisplayName({ displayName: item.displayName, title: item.name || item.title })
      : item.name || item.title,
  description: item.shortDescription || item.description,
  logoUrl: item.logoUrl,
  taxonomyId: item.taxonomyId,
  linkToDetails: getLinkToDetails(item),
});

interface ListProps extends Partial<ListModel> {
  listId: string;
  name: string;
  listType: ListType;
  itemCount?: number;
  fullItems?: ItemModel[];
  tags?: string[];
  allowEdit?: boolean;
  isAdmin?: boolean;
  allowDeletion?: boolean;
  allowItemDeletion?: boolean;
}

export const List: FC<ListProps> = ({
  name,
  listType,
  description,
  visibility,
  allowedDomains,
  itemCount = 0,
  fullItems,
  tags,
  listId,
  evaluationRules,
  ownerAlias,
  ownershipBindle,
  sharedWithBindle,
  isAdmin = false,
  allowEdit = false,
  allowDeletion = false,
  allowItemDeletion = false,
}) => {
  const { deleteList } = useLists();
  const { addMessageBanner } = useMessageBannersBox();
  const jobFamily = getJobFamilyFromRule(evaluationRules);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 9;
  const totalPages = fullItems ? Math.ceil(fullItems.length / itemsPerPage) : 0;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = fullItems?.slice(startIndex, startIndex + itemsPerPage);

  const handleConfirmDelete = async (close: () => void) => {
    try {
      await deleteList(listId, listType);
      addMessageBanner(getString("lists.deleteSuccess"), MessageBannerType.Success, 5000);
      close();
    } catch (error) {
      addMessageBanner(getString("lists.deleteError"), MessageBannerType.Error);
      close();
    }
  };

  const renderDeleteModal = ({ close }: RenderModalFunctionParams) => (
    <ModalContent
      titleText={getString("lists.deleteConfirmation.title")}
      buttons={[
        <Button key="cancel" touchpoint="list-delete-cancel" variant={ButtonVariant.Secondary} onClick={close}>
          {getString("lists.deleteConfirmation.cancel")}
        </Button>,
        <Button
          key="confirm"
          touchpoint="list-delete-confirm"
          variant={ButtonVariant.Primary}
          onClick={() => handleConfirmDelete(close)}
          data-cy="delete-confirmation-button"
        >
          {getString("lists.deleteConfirmation.confirm")}
        </Button>,
      ]}
    >
      <Text>{getString("lists.deleteConfirmation.message", { name })}</Text>
      {jobFamily && (
        <>
          <Spacer height="S200" />
          <Text>{getString("lists.deleteConfirmation.adminMessage", { jobFamily })}</Text>
        </>
      )}
    </ModalContent>
  );

  const renderVisibility = () => {
    switch (visibility) {
      case Visibility.PRIVATE:
        return (
          <Chip icon={<IconPadlock />} backgroundColor={ChipBackgroundColor.Purple} data-testid="list-visibility">
            {getString("lists.visibility.private")}
          </Chip>
        );
      case Visibility.PUBLIC:
        return (
          <Chip icon={<IconPeople />} backgroundColor={ChipBackgroundColor.Blue} data-testid="list-visibility">
            {getString("lists.visibility.public")}
          </Chip>
        );
      case Visibility.SHARED:
        return (
          <Chip icon={<IconGlobe />} backgroundColor={ChipBackgroundColor.Green} data-testid="list-visibility">
            {getString("lists.visibility.shared")}
          </Chip>
        );
      default:
        return visibility;
    }
  };

  const renderTitle = ({ titleText, toggle }: ExpanderTitleRendererParams) => {
    return (
      <Row width="100%" justifyContent="space-between">
        <Col onClick={toggle} data-testid="list-expander-trigger">
          <Text fontSize="T300" fontWeight="bold" data-testid="list-name">
            {titleText}
          </Text>

          <Text fontSize="T200" data-testid="list-description">
            {description}
          </Text>
        </Col>
        <Row alignItems="center" gridGap="S300">
          <Chip data-testid="list-item-count">
            {getString("lists.itemCount", { itemCount: itemCount || fullItems?.length || 0 })}
          </Chip>
          {visibility && renderVisibility()}
          {jobFamily && <Chip backgroundColor={ChipBackgroundColor.Blue}>{jobFamily}</Chip>}
          {ownerAlias && (
            <Chip backgroundColor={ChipBackgroundColor.Yellow}>{getString("lists.owner", { ownerAlias })}</Chip>
          )}
          {allowDeletion && (
            <WithModal renderModal={renderDeleteModal}>
              {({ open }) => (
                <Button
                  touchpoint="list-delete-button"
                  variant={ButtonVariant.Secondary}
                  onClick={open}
                  data-cy="list-delete-button"
                  data-testid="list-delete-button"
                >
                  {getString("lists.deleteList")}
                </Button>
              )}
            </WithModal>
          )}
          {allowEdit && (
            <ListModalButton
              initialData={{
                name,
                description,
                visibility,
                allowedDomains,
                items: fullItems,
                tags,
                evaluationRules,
                ownershipBindle,
                sharedWithBindle,
              }}
              disable={ownerAlias !== UserPermissions.current.username}
              listId={listId}
              type={isAdmin ? LIST_MODAL_TYPE.ADMIN_EDIT : LIST_MODAL_TYPE.EDIT}
              variant={ButtonVariant.Primary}
            />
          )}
        </Row>
      </Row>
    );
  };

  return (
    <View data-testid="list-container">
      <Expander
        renderTitle={renderTitle}
        titleText={name}
        chevronPosition={ExpanderChevronPosition.Leading}
        data-testid="list-expander"
      >
        <GridContainer data-testid="list-items-grid">
          {paginatedItems ? (
            paginatedItems.map((item, index) => (
              <GridItem lg={4} md={4} key={`${item.taxonomyId}-${index}`}>
                <ListItem
                  listId={listId}
                  listName={name}
                  listType={listType}
                  item={normalizeListItem(item, allowedDomains?.[0])}
                  allowDeletion={allowItemDeletion}
                />
              </GridItem>
            ))
          ) : (
            <GridItem lg={4} md={4}>
              <Text>No Items</Text>
            </GridItem>
          )}
        </GridContainer>
        {totalPages > 1 && (
          <>
            <Spacer height="S300" />
            <Row justifyContent="center">
              <Pagination selectedPage={currentPage} numberOfPages={totalPages} onPageSelect={setCurrentPage} />
            </Row>
          </>
        )}
      </Expander>
      <Spacer height="S300" />
      <Hr />
    </View>
  );
};
